import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Extended role types for multi-tenant architecture
type UserRole = 'super_admin' | 'local_admin' | 'admin' | 'facilitator' | 'client' | null;

export function useUserRole() {
  const [role, setRole] = useState<UserRole>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserRole = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      console.log('[useUserRole] Fetching role for user:', user?.id);

      if (!user) {
        setRole(null);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('[useUserRole] Error fetching user role:', error);
        setRole(null);
      } else if (data) {
        console.log('[useUserRole] Role found:', data.role);
        setRole(data.role as UserRole);
      } else {
        console.log('[useUserRole] No role found, checking profiles table');
        // Fallback: check profiles table for role
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        if (profile?.role) {
          console.log('[useUserRole] Role from profiles:', profile.role);
          setRole(profile.role as UserRole);
        } else {
          setRole(null);
        }
      }
    } catch (error) {
      console.error('[useUserRole] Error in fetchUserRole:', error);
      setRole(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserRole();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[useUserRole] Auth state changed:', event);
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        fetchUserRole();
      } else if (event === 'SIGNED_OUT') {
        setRole(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchUserRole]);

  return {
    role,
    // Super admin has full system access
    isSuperAdmin: role === 'super_admin',
    // Local admin manages a specific treatment center
    isLocalAdmin: role === 'local_admin',
    // Legacy admin check (now maps to local_admin or super_admin)
    isAdmin: role === 'admin' || role === 'local_admin' || role === 'super_admin',
    // Facilitator or any admin can access facilitator features
    isFacilitator: role === 'facilitator' || role === 'admin' || role === 'local_admin' || role === 'super_admin',
    // Client check
    isClient: role === 'client',
    // Any admin role (super, local, or legacy)
    isAnyAdmin: role === 'super_admin' || role === 'local_admin' || role === 'admin',
    isLoading
  };
}
